import {
  AnimatePresence,
  motion,
  type Transition,
  type Variants,
} from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useOutlet } from 'react-router-dom';

const variants: Variants = {
  initial: (direction: number) => ({
    y: direction > 0 ? '100vh' : '-100vh',
    opacity: 0,
  }),
  animate: {
    y: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    y: direction > 0 ? '-100vh' : '100vh',
    opacity: 0,
  }),
};

const transition: Transition = {
  y: { type: 'tween', duration: 0.35, ease: 'easeInOut' },
  opacity: { duration: 0.2 },
};

/**
 * Captures the outlet element on mount and freezes it so that
 * exit animations render the old page content, not the new one.
 */
const FrozenOutlet = () => {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
};

const ScrollFlowLayout: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const directionRef = useRef(0);

  const isBackwards = navigationType === 'POP';
  directionRef.current = isBackwards ? -1 : 1;

  const pathname = location.pathname;
  useEffect(() => {
    if (pathname) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return (
    <div style={{ position: 'relative', overflow: 'hidden', height: '100%' }}>
      <AnimatePresence
        mode='popLayout'
        initial={false}
        custom={directionRef.current}
      >
        <motion.div
          key={location.pathname}
          custom={directionRef.current}
          variants={variants}
          initial='initial'
          animate='animate'
          exit='exit'
          transition={transition}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <FrozenOutlet />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ScrollFlowLayout;
